import type { WizardStepStatus } from "./WizardCard";
import type { TripFormValues } from "./schema";

// =============================================================================
// Wizard step machine helpers
//
// Single source of truth for the step list, conditional step inclusion, and
// per-step status calculation. Lifted out of `TripForm` so the orchestration
// rules are easy to read in isolation.
//
// Conditional steps:
//   - "return"    only when tripType === "round_trip".
//   - "multiLeg"  only when tripType === "multi_leg".
//
// The "is this the right patient?" verification lives inside the lookup
// step itself (folded into the same card) rather than as a separate
// stop, so dispatch only sees the patient name once and clicks Yes/No
// without an extra Continue.
// =============================================================================

export type StepKey =
  | "lookup"
  | "details"
  | "tripType"
  | "transportType"
  | "transportDate"
  | "pickup"
  | "dropoff"
  | "return"
  | "multiLeg"
  | "requester"
  | "submit";

export interface BuildStepsInput {
  tripType: string;
}

export function buildStepList({ tripType }: BuildStepsInput): StepKey[] {
  const steps: StepKey[] = [
    "lookup",
    "details",
    "tripType",
    "transportType",
    "transportDate",
    "pickup",
    "dropoff",
  ];
  if (tripType === "round_trip") steps.push("return");
  if (tripType === "multi_leg") steps.push("multiLeg");
  steps.push("requester", "submit");
  return steps;
}

/** Return the visual status for `step` given the active step and the set
 *  of steps the user has previously answered.
 *
 *  Steps whose key isn't in the active list are always "pending" (i.e.
 *  not rendered).
 *
 *  Why an explicit `answered` set rather than an index comparison: when
 *  the user clicks Edit on an early step, the wizard moves backwards.
 *  Steps that were already answered should keep their "complete"
 *  summary visible so the user can see they're not losing data, instead
 *  of disappearing into "pending" until they walk forward again. */
export function statusFor(
  steps: StepKey[],
  current: StepKey,
  answered: ReadonlySet<StepKey>,
  step: StepKey,
): WizardStepStatus {
  if (!steps.includes(step)) return "pending";
  if (step === current) return "active";
  if (answered.has(step)) return "complete";
  return "pending";
}

/** Step number (1-based) for display in the WizardCard badge. Falls back to
 *  the step's position in a fully-expanded list if the conditional steps
 *  aren't active yet, so the numbering only ever moves forward as the user
 *  advances. */
export function displayIndex(steps: StepKey[], step: StepKey): number {
  const idx = steps.indexOf(step);
  return idx === -1 ? 0 : idx + 1;
}

/** Locate the next step after `current` in the dynamic list. Returns the
 *  same step if `current` is already at the end. */
export function nextStep(steps: StepKey[], current: StepKey): StepKey {
  const idx = steps.indexOf(current);
  if (idx === -1) return steps[0]!;
  if (idx >= steps.length - 1) return current;
  return steps[idx + 1]!;
}

/** Locate the previous step (used by Edit affordances). */
export function prevStep(steps: StepKey[], current: StepKey): StepKey {
  const idx = steps.indexOf(current);
  if (idx <= 0) return steps[0]!;
  return steps[idx - 1]!;
}

// The step that asks each answer, so Review can send the booker back to the
// one that no longer passes. MRN and DOB are left out: reopening the lookup
// clears the whole form. So are the values the booker never sees.
const FIELD_STEP: Partial<Record<keyof TripFormValues, StepKey>> = {
  department: "details",
  costCenter: "details",
  firstName: "details",
  lastName: "details",
  tripType: "tripType",
  transportType: "transportType",
  wheelchairSource: "transportType",
  transportDate: "transportDate",
  repeatEnabled: "transportDate",
  repeatWeekdays: "transportDate",
  repeatWeeks: "transportDate",
  repeatDates: "transportDate",
  pickupAddress: "pickup",
  pickupCityZip: "pickup",
  pickupLocation: "pickup",
  pickupPlace: "pickup",
  contactPhone: "pickup",
  pickupWillCall: "pickup",
  timeType: "pickup",
  primaryTime: "pickup",
  shortNoticeReason: "pickup",
  pickupNotes: "pickup",
  companionCount: "pickup",
  destinationAddress: "dropoff",
  destinationCityZip: "dropoff",
  destinationPlace: "dropoff",
  dropoffNotes: "dropoff",
  returnTimeMode: "return",
  returnPickupTime: "return",
  returnToOriginalPickup: "return",
  returnCustomDestinationAddress: "return",
  returnCustomDestinationCityZip: "return",
  returnDestinationPlace: "return",
  returnLegNotes: "return",
  additionalLegs: "multiLeg",
  requestedBy: "requester",
  callbackPhone: "requester",
  requesterEmail: "requester",
};

/** The answers `step` asks. Review marks only these when it sends the booker
 *  back: an error marked on a later step stays until that answer is checked
 *  again, even once an earlier fix (a new date) has made it right. */
export function fieldsOf(step: StepKey): Array<keyof TripFormValues> {
  return (Object.keys(FIELD_STEP) as Array<keyof TripFormValues>).filter(
    (k) => FIELD_STEP[k] === step,
  );
}

/** Each step's title, as its card shows it once answered. */
export const STEP_TITLE: Record<StepKey, string> = {
  lookup: "Patient identifier",
  details: "Patient details",
  tripType: "Trip type",
  transportType: "Level of service",
  transportDate: "Transport date",
  pickup: "Pick-up",
  dropoff: "Drop-off",
  return: "Return trip",
  multiLeg: "Additional legs",
  requester: "Requester",
  submit: "Review & submit",
};

/** The earliest step in `steps` with an answer that fails the full check,
 *  and what's wrong with it. An answer can pass its own step and fail later:
 *  a pickup that became too soon, or a draft restored the next day. Null when
 *  no failing answer belongs to one of `steps`. */
export function firstProblem(
  steps: StepKey[],
  issues: ReadonlyArray<{ path: ReadonlyArray<PropertyKey>; message: string }>,
): { step: StepKey; message: string } | null {
  let found: { step: StepKey; message: string } | null = null;
  for (const issue of issues) {
    const step = FIELD_STEP[issue.path[0] as keyof TripFormValues];
    if (!step || !steps.includes(step)) continue;
    if (!found || steps.indexOf(step) < steps.indexOf(found.step)) {
      found = { step, message: issue.message };
    }
  }
  return found;
}
